import { useEffect, useRef } from 'react'

const DEFAULT_TIMER = 600
const useAutoScrollCards = ({ cards = [] }) => {
  const scrollContainerRef = useRef(null)
  const autoScrollIntervalRef = useRef(null)
  const userInteractionTimeoutRef = useRef(null)
  const isUserInteractingRef = useRef(false)
  const currentCardIndexRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || cards?.length === 0) return

    isMountedRef.current = true

    const scrollToNextCard = () => {
      if (!isMountedRef.current || !container || isUserInteractingRef.current)
        return

      const cardElements = container.querySelectorAll('[data-card]')
      if (cardElements.length === 0) return

      currentCardIndexRef.current =
        (currentCardIndexRef.current + 1) % cardElements.length

      const nextCard = cardElements[currentCardIndexRef.current]
      if (nextCard) {
        const containerRect = container.getBoundingClientRect()
        const cardRect = nextCard.getBoundingClientRect()
        const scrollLeft =
          cardRect.left - containerRect.left + container.scrollLeft - 16

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        })
      }
    }

    const startAutoScroll = () => {
      if (autoScrollIntervalRef.current) return
      autoScrollIntervalRef.current = setInterval(
        scrollToNextCard,
        DEFAULT_TIMER,
      )
    }

    const stopAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current)
        autoScrollIntervalRef.current = null
      }
    }

    const handleUserScroll = () => {
      isUserInteractingRef.current = true
      stopAutoScroll()

      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current)
        userInteractionTimeoutRef.current = null
      }

      userInteractionTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          isUserInteractingRef.current = false
          startAutoScroll()
        }
      }, DEFAULT_TIMER)
    }

    const handleMouseEnter = () => {
      isUserInteractingRef.current = true
      stopAutoScroll()
    }

    const handleMouseLeave = () => {
      isUserInteractingRef.current = false
      startAutoScroll()
    }

    container.addEventListener('scroll', handleUserScroll, { passive: true })
    container.addEventListener('mouseenter', handleMouseEnter)
    container.addEventListener('mouseleave', handleMouseLeave)
    container.addEventListener('touchstart', handleUserScroll, {
      passive: true,
    })

    startAutoScroll()

    return () => {
      isMountedRef.current = false
      stopAutoScroll()
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current)
        userInteractionTimeoutRef.current = null
      }
      container.removeEventListener('scroll', handleUserScroll)
      container.removeEventListener('mouseenter', handleMouseEnter)
      container.removeEventListener('mouseleave', handleMouseLeave)
      container.removeEventListener('touchstart', handleUserScroll)
    }
  }, [cards?.length])

  return {
    scrollContainerRef,
  }
}
export default useAutoScrollCards
